import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown,
  UserPlus, UserMinus, CreditCard, Download, Users, RefreshCw, Inbox,
} from 'lucide-react';


import { getApiUrl } from '../../config/api';


// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────
const API_URL = getApiUrl('/admin/dashboard');
const getToken = () => localStorage.getItem('access_token') || '';


// ─────────────────────────────────────────────────────────────────────────────
// THEME TOKENS
// ─────────────────────────────────────────────────────────────────────────────
const t = {
  bg:              '#0d0e17',
  sidebar:         '#10111c',
  card:            '#181929',
  cardHover:       '#1d1e30',
  cardBorder:      'rgba(255,255,255,0.07)',
  cardBorderHover: 'rgba(255,255,255,0.14)',
  text:            '#ffffff',
  textMuted:       '#8b8fa8',
  textFaint:       '#4b4f6a',
  accent:          '#22d3b4',
  accentDim:       'rgba(34,211,180,0.10)',
  accentBorder:    'rgba(34,211,180,0.35)',
  green:           '#10b981',
  red:             '#f43f5e',
  amber:           '#f59e0b',
  blue:            '#3b82f6',
  purple:          '#a855f7',
};


// ─────────────────────────────────────────────────────────────────────────────
// RISK LABEL → BADGE STYLE (colors chosen to match the previous score-based badges)
// ─────────────────────────────────────────────────────────────────────────────
const RISK_LABEL_STYLES = {
  Red:   { bg: 'rgba(244,63,94,0.13)',  color: '#f43f5e', border: 'rgba(244,63,94,0.28)'  },
  Amber: { bg: 'rgba(245,158,11,0.13)', color: '#f59e0b', border: 'rgba(245,158,11,0.28)' },
  Green: { bg: 'rgba(16,185,129,0.13)', color: '#10b981', border: 'rgba(16,185,129,0.28)' },
};

// Parses a combined "85 / Red" string from the API into a display value + badge style
const parseRiskScore = (riskScoreStr) => {
  if (!riskScoreStr) {
    return { display: '—', ...{ bg: 'rgba(255,255,255,0.06)', color: t.textFaint, border: 'rgba(255,255,255,0.12)' } };
  }
  const match = riskScoreStr.match(/(\d+)\s*\/\s*(\w+)/);
  const label = match ? match[2] : null;
  const style = RISK_LABEL_STYLES[label] || RISK_LABEL_STYLES.Green;
  return { display: riskScoreStr, ...style };
};


// Determines the sign/color of an MRR movement value string (e.g. "+$18.4k", "-$3.1k")
const mrrSignStyle = (value) => {
  if (value === null || value === undefined) return { color: t.textFaint, dot: t.textFaint, display: 'N/A (flat plan)' };
  if (typeof value === 'string' && value.trim().startsWith('+') && value.trim() !== '+$0') {
    return { color: t.green, dot: t.green, display: value };
  }
  if (typeof value === 'string' && value.trim().startsWith('-') && value.trim() !== '-$0') {
    return { color: t.red, dot: t.amber, display: value };
  }
  return { color: t.textMuted, dot: t.blue, display: value };
};


// ─────────────────────────────────────────────────────────────────────────────
// REUSABLE: CARD WRAPPER
// ─────────────────────────────────────────────────────────────────────────────
const Card = ({ children, style = {}, className = '' }) => (
  <div
    className={`ad-card ${className}`}
    style={{
      background: t.card,
      border: `1px solid ${t.cardBorder}`,
      ...style,
    }}
  >
    {children}
  </div>
);


const CardHeader = ({ title, subtitle, action }) => (
  <div className="ad-card-header" style={{
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '20px',
  }}>
    <div>
      <h3 style={{ fontSize: '15px', fontWeight: 800, color: t.text, margin: '0 0 3px' }}>{title}</h3>
      {subtitle && <p style={{ fontSize: '12px', color: t.textFaint, margin: 0 }}>{subtitle}</p>}
    </div>
    {action}
  </div>
);


// Small inline empty/loading state block, since this file has no external CSS to draw from
const StateBlock = ({ loading, empty, emptyText = 'No data available yet', loadingText = 'Loading...' }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '8px', padding: '28px 12px', color: t.textFaint, fontSize: '12.5px', textAlign: 'center',
  }}>
    {loading ? <RefreshCw size={20} className="admin-spin" /> : <Inbox size={22} />}
    <span>{loading ? loadingText : emptyText}</span>
  </div>
);


// ─────────────────────────────────────────────────────────────────────────────
// KPI CARD
// ─────────────────────────────────────────────────────────────────────────────
const KpiCard = ({ title, value, change, changeLabel, positive, icon: Icon, iconColor, loading }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      className="ad-kpi-card"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? t.cardHover : t.card,
        border: `1px solid ${hov ? t.cardBorderHover : t.cardBorder}`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: t.textMuted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {title}
        </span>
        <div style={{
          width: '34px', height: '34px', borderRadius: '10px',
          background: `${iconColor}1a`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: iconColor,
        }}>
          <Icon size={17} />
        </div>
      </div>
      <div className="ad-kpi-val" style={{ fontSize: '30px', fontWeight: 800, color: t.text, marginBottom: '10px', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
        {loading ? '—' : value}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: positive ? t.green : t.red, display: 'flex', alignItems: 'center', gap: '2px' }}>
          {positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
          {loading ? '—' : change}
        </span>
        <span style={{ fontSize: '11px', color: t.textFaint }}>{changeLabel}</span>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// REVENUE AREA CHART (pure SVG — no library needed)
// Now driven by revenue_trend { months, revenue, customers, churn } from the API
// ─────────────────────────────────────────────────────────────────────────────
const RevenueChart = ({ activeTab, setActiveTab, trend, loading }) => {
  const safeTrend = trend || { months: [], revenue: [], customers: [], churn: [] };
  const datasets = {
    Revenue:   Array.isArray(safeTrend.revenue) ? safeTrend.revenue : [],
    Customers: Array.isArray(safeTrend.customers) ? safeTrend.customers : [],
    Churn:     Array.isArray(safeTrend.churn) ? safeTrend.churn : [],
  };
  const data   = datasets[activeTab];
  const months = Array.isArray(safeTrend.months) ? safeTrend.months : [];
  const labels = months.slice(0, data.length);

  const hasEnoughData = data.length >= 2;
  const maxVal = data.length ? Math.max(...data, 0) || 1 : 1;

  const W = 560, H = 175;
  const PAD = { top: 12, bottom: 22, left: 40, right: 8 };
  const xStep  = hasEnoughData ? (W - PAD.left - PAD.right) / (data.length - 1) : 0;
  const yScale = v => PAD.top + (H - PAD.top - PAD.bottom) * (1 - v / (maxVal * 1.15));
  const pts    = hasEnoughData ? data.map((v, i) => `${PAD.left + i * xStep},${yScale(v)}`).join(' ') : '';
  const area   = hasEnoughData
    ? `${PAD.left},${H - PAD.bottom} ${pts} ${PAD.left + (data.length - 1) * xStep},${H - PAD.bottom}`
    : '';
  const yTicks = [0, Math.round(maxVal * 0.33), Math.round(maxVal * 0.67), maxVal].reverse();
  const fmt = v => {
    if (activeTab === 'Revenue') return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`;
    if (activeTab === 'Customers') return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`;
    return `${v}%`;
  };


  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '18px' }}>
        {['Revenue', 'Customers', 'Churn'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '5px 15px', borderRadius: '999px', border: 'none',
            fontSize: '12px', fontWeight: 700, cursor: 'pointer',
            background: activeTab === tab ? t.accent : 'rgba(255,255,255,0.06)',
            color: activeTab === tab ? '#000' : t.textMuted,
            transition: 'all 0.18s',
          }}>{tab}</button>
        ))}
      </div>

      {loading ? (
        <StateBlock loading loadingText="Loading revenue trend..." />
      ) : !hasEnoughData ? (
        <StateBlock empty emptyText="Not enough trend data yet" />
      ) : (
        <div className="ad-chart-svg-wrapper">
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '175px', overflow: 'visible', display: 'block' }}>
            <defs>
              <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={t.accent} stopOpacity="0.3" />
                <stop offset="100%" stopColor={t.accent} stopOpacity="0.01" />
              </linearGradient>
            </defs>
            {yTicks.map((tick, i) => {
              const y = yScale(tick);
              return (
                <g key={i}>
                  <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
                    stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeDasharray="4,5" />
                  <text x={PAD.left - 5} y={y + 3.5} textAnchor="end"
                    fill={t.textFaint} fontSize="8.5" fontFamily="Inter,sans-serif">
                    {fmt(tick)}
                  </text>
                </g>
              );
            })}
            <polygon points={area} fill="url(#areaGrad)" />
            <polyline points={pts} fill="none" stroke={t.accent}
              strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
            {data.map((v, i) => (
              <circle key={i}
                cx={PAD.left + i * xStep} cy={yScale(v)} r="3.5"
                fill={t.accent} stroke={t.card} strokeWidth="1.5"
                opacity="0"
                onMouseEnter={e => { e.target.style.opacity = 1; }}
                onMouseLeave={e => { e.target.style.opacity = 0; }}
                style={{ cursor: 'crosshair', transition: 'opacity 0.15s' }}
              />
            ))}
            {labels.map((l, i) => (
              <text key={i} x={PAD.left + i * xStep} y={H - 5}
                textAnchor="middle" fill={t.textFaint} fontSize="8.5" fontFamily="Inter,sans-serif">
                {l}
              </text>
            ))}
          </svg>
        </div>
      )}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// MRR MOVEMENT — now driven by mrr_movement from the API
// ─────────────────────────────────────────────────────────────────────────────
const MrrMovement = ({ movement, loading }) => {
  if (loading) return <StateBlock loading loadingText="Loading MRR movement..." />;
  if (!movement) return <StateBlock empty emptyText="No MRR movement data available" />;

  const items = [
    { label: 'New MRR',     ...mrrSignStyle(movement.new_mrr) },
    { label: 'Expansion',   ...mrrSignStyle(movement.expansion) },
    { label: 'Contraction', ...mrrSignStyle(movement.contraction) },
    { label: 'Churned',     ...mrrSignStyle(movement.churned) },
  ];

  return (
    <div>
      {items.map((item, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '13px 0',
          borderBottom: i < items.length - 1 ? `1px solid ${t.cardBorder}` : 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.dot, display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: t.textMuted }}>{item.label}</span>
          </div>
          <span style={{ fontSize: '13px', fontWeight: 700, color: item.color }}>{item.display}</span>
        </div>
      ))}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingTop: '16px', marginTop: '4px',
        borderTop: `1px solid ${t.cardBorder}`,
      }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Net MRR
        </span>
        <span style={{ fontSize: '26px', fontWeight: 900, color: t.text, letterSpacing: '-0.02em' }}>
          {movement.net_mrr ?? '—'}
        </span>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// ONBOARDING FUNNEL — now driven by onboarding_funnel { signed_up, email_verified, profile_completed }
// Drop-off % is computed client-side as the difference between consecutive steps,
// since the API only returns the 3 raw percentages, not the drop deltas.
// ─────────────────────────────────────────────────────────────────────────────
const OnboardingFunnel = ({ funnel, loading }) => {
  if (loading) return <StateBlock loading loadingText="Loading onboarding funnel..." />;
  if (!funnel) return <StateBlock empty emptyText="No onboarding funnel data available" />;

  const rawSteps = [
    { label: 'Signed Up',         pct: funnel.signed_up ?? 0 },
    { label: 'Email Verified',    pct: funnel.email_verified ?? 0 },
    { label: 'Profile Completed', pct: funnel.profile_completed ?? 0 },
  ];

  const steps = rawSteps.map((step, i) => {
    const prevPct = i === 0 ? null : rawSteps[i - 1].pct;
    const drop = prevPct === null ? null : Math.round((prevPct - step.pct) * 10) / 10;
    return {
      ...step,
      drop: drop === null ? null : `-${drop}% drop-off`,
      highlight: i === rawSteps.length - 1,
    };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {steps.map((step, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '7px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: step.highlight ? t.accent : t.text }}>
              {step.label}
            </span>
            <span style={{ fontSize: '13px', fontWeight: 700, color: step.highlight ? t.accent : t.text }}>
              {step.pct}%
            </span>
          </div>
          <div style={{ height: '7px', background: 'rgba(255,255,255,0.06)', borderRadius: '999px', overflow: 'hidden', marginBottom: '4px' }}>
            <div style={{
              height: '100%', width: `${step.pct}%`,
              background: `linear-gradient(90deg, ${t.accent}, rgba(34,211,180,0.45))`,
              borderRadius: '999px',
              boxShadow: step.highlight ? `0 0 8px ${t.accent}55` : 'none',
              transition: 'width 1s ease',
            }} />
          </div>
          {step.drop && (
            <div style={{ textAlign: 'right', fontSize: '10.5px', color: t.textFaint }}>{step.drop}</div>
          )}
        </div>
      ))}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// AT-RISK CUSTOMERS — now driven by at_risk_customers from the API
// risk_score arrives as a combined string (e.g. "85 / Red"), parsed for badge styling
// ─────────────────────────────────────────────────────────────────────────────
const AtRiskRow = ({ row, isLast }) => {
  const [hov, setHov] = useState(false);
  const badge = parseRiskScore(row.risk_score);
  return (
    <tr
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderBottom: isLast ? 'none' : `1px solid ${t.cardBorder}`,
        background: hov ? 'rgba(255,255,255,0.025)' : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      <td style={{ padding: '13px 12px', fontSize: '13px', color: t.text, fontWeight: 500 }}>{row.email}</td>
      <td style={{ padding: '13px 12px', fontSize: '13px', color: t.textMuted }}>{row.reason}</td>
      <td style={{ padding: '13px 12px', textAlign: 'right' }}>
        <span style={{
          background: badge.bg, color: badge.color,
          padding: '4px 11px', borderRadius: '999px',
          fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
          border: `1px solid ${badge.border}`,
        }}>
          {badge.display}
        </span>
      </td>
    </tr>
  );
};


const AtRiskCustomers = ({ rows, loading }) => {
  if (loading) return <StateBlock loading loadingText="Loading at-risk customers..." />;
  if (!rows || rows.length === 0) return <StateBlock empty emptyText="No at-risk customers flagged right now" />;

  return (
    <div className="ad-table-scroll">
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '400px' }}>
        <thead>
          <tr>
            {['Email', 'Reason', 'Risk Score'].map((h, i) => (
              <th key={h} style={{
                padding: '8px 12px',
                fontSize: '10px', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.09em',
                color: t.textFaint, textAlign: i === 2 ? 'right' : 'left',
                borderBottom: `1px solid ${t.cardBorder}`,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <AtRiskRow key={i} row={row} isLast={i === rows.length - 1} />
          ))}
        </tbody>
      </table>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// RECENT SIGNUPS — no matching field in the new /admin/dashboard schema,
// left as static placeholder data until the backend exposes it.
// ─────────────────────────────────────────────────────────────────────────────
const planStyles = {
  Elite:   { bg: 'rgba(34,211,180,0.10)',  color: '#22d3b4', border: 'rgba(34,211,180,0.28)'  },
  Premium: { bg: 'rgba(168,85,247,0.10)',  color: '#a855f7', border: 'rgba(168,85,247,0.28)'  },
  Basic:   { bg: 'rgba(255,255,255,0.06)', color: '#8b8fa8', border: 'rgba(255,255,255,0.12)' },
};
const statusStyles = {
  Active:   { color: '#10b981', dot: '#10b981' },
  Pending:  { color: '#f59e0b', dot: '#f59e0b' },
  Inactive: { color: '#f43f5e', dot: '#f43f5e' },
};


const SignupRow = ({ row, isLast }) => {
  const [hov, setHov] = useState(false);
  const plan = planStyles[row.plan] || planStyles.Basic;
  const st   = statusStyles[row.status] || statusStyles.Active;
  return (
    <tr
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderBottom: isLast ? 'none' : `1px solid ${t.cardBorder}`,
        background: hov ? 'rgba(255,255,255,0.025)' : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      <td style={{ padding: '15px 16px', fontSize: '13px', color: t.text, fontWeight: 500 }}>{row.email}</td>
      <td style={{ padding: '15px 16px', fontSize: '13px', color: t.textMuted, whiteSpace: 'nowrap' }}>{row.date}</td>
      <td style={{ padding: '15px 16px' }}>
        <span style={{
          background: plan.bg, color: plan.color,
          padding: '4px 11px', borderRadius: '6px',
          fontSize: '11px', fontWeight: 700,
          border: `1px solid ${plan.border}`,
        }}>{row.plan}</span>
      </td>
      <td style={{ padding: '15px 16px', fontSize: '13px', color: t.textMuted }}>{row.country}</td>
      <td style={{ padding: '15px 16px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: st.color, fontWeight: 600 }}>
          <span style={{
            width: '7px', height: '7px', borderRadius: '50%',
            background: st.dot, display: 'inline-block', flexShrink: 0,
            boxShadow: `0 0 5px ${st.dot}88`,
          }} />
          {row.status}
        </span>
      </td>
    </tr>
  );
};


const RecentSignups = ({ signups = [], loading = false }) => {
  if (loading) return <StateBlock loading loadingText="Loading recent signups..." />;
  if (!signups || signups.length === 0) return <StateBlock empty emptyText="No recent signups found" />;

  return (
    <div className="ad-table-scroll">
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
        <thead>
          <tr>
            {['Email', 'Signup Date', 'Plan', 'Country', 'Status'].map(h => (
              <th key={h} style={{
                padding: '8px 16px',
                fontSize: '10px', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.09em',
                color: t.textFaint, textAlign: 'left',
                borderBottom: `1px solid ${t.cardBorder}`,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {signups.map((row, i) => (
            <SignupRow key={row.id || row.email || i} row={row} isLast={i === signups.length - 1} />
          ))}
        </tbody>
      </table>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// ROOT: AdminDashboard
// ─────────────────────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const [activeTab,     setActiveTab]     = useState('Revenue');
  const [isMobile,      setIsMobile]      = useState(false);

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(API_URL, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = await res.json();
      setDashboardData(data);
    } catch (err) {
      console.error('Admin dashboard fetch error:', err);
      setError('Unable to load admin dashboard data from the server.');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);


  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 992);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);


  const mrr = dashboardData?.mrr ?? 0;
  const mrrGrowth = dashboardData?.mrr_growth ?? 0;
  const totalCustomers = dashboardData?.total_customers ?? 0;
  const customersGrowth = dashboardData?.customers_growth ?? 0;
  const newThisMonth = dashboardData?.new_this_month ?? 0;
  const newGrowth = dashboardData?.new_growth ?? 0;
  const churnedThisMonth = dashboardData?.churned_this_month ?? 0;
  const churnedGrowth = dashboardData?.churned_growth ?? 0;
  const revenueTrend = dashboardData?.revenue_trend ?? null;
  const mrrMovement = dashboardData?.mrr_movement ?? null;
  const onboardingFunnel = dashboardData?.onboarding_funnel ?? null;
  const atRiskCustomers = Array.isArray(dashboardData?.at_risk_customers) ? dashboardData.at_risk_customers : [];


  return (
    <div className="ad-wrapper">
      <style>{`
        @keyframes admin-spin-kf { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .admin-spin { animation: admin-spin-kf 1s linear infinite; }

        .ad-wrapper {
          display: flex;
          min-height: 100vh;
          width: 100%;
          max-width: 100vw;
          box-sizing: border-box;
          background: #08090d;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #f1f3f9;
          font-size: 14px;
          overflow-x: hidden;
        }

        .ad-scroll-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
          width: 100%;
          max-width: 100vw;
          overflow-x: hidden;
          box-sizing: border-box;
        }

        .ad-main-content {
          padding: 24px 28px 60px 28px;
          display: flex;
          flex-direction: column;
          gap: 20px;
          width: 100%;
          box-sizing: border-box;
          min-width: 0;
        }

        .ad-grid-row {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }

        .ad-card {
          background: #111318;
          border: 1px solid rgba(255, 255, 255, 0.07);
          border-radius: 16px;
          padding: 24px;
          box-sizing: border-box;
          min-width: 0;
          overflow: hidden;
        }

        .ad-kpi-card {
          border-radius: 16px;
          padding: 22px 24px;
          flex: 1 1 calc(25% - 12px);
          min-width: 0;
          transition: all 0.2s;
          cursor: default;
          box-sizing: border-box;
        }

        .ad-card-chart { flex: 2 1 400px; }
        .ad-card-mrr { flex: 1 1 240px; }
        .ad-card-funnel { flex: 1 1 280px; }
        .ad-card-at-risk { flex: 1.6 1 340px; }
        .ad-card-signups { width: 100%; }

        .ad-table-scroll {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          box-sizing: border-box;
        }

        .ad-chart-svg-wrapper {
          width: 100%;
          overflow: visible;
        }

        @media (max-width: 860px) {
          .ad-main-content {
            padding: 16px 12px 60px 12px;
            gap: 14px;
          }
          .ad-grid-row {
            gap: 12px;
          }
          .ad-card {
            padding: 18px 14px;
          }
          .ad-card-chart,
          .ad-card-mrr,
          .ad-card-funnel,
          .ad-card-at-risk,
          .ad-card-signups {
            flex: 1 1 100% !important;
            width: 100% !important;
          }
          .ad-kpi-card {
            padding: 16px 14px;
            border-radius: 12px;
            flex: 1 1 calc(50% - 8px);
          }
          .ad-kpi-val {
            font-size: 22px;
          }
        }

        @media (max-width: 480px) {
          .ad-main-content {
            padding: 12px 8px 60px 8px;
            gap: 12px;
          }
          .ad-card {
            padding: 14px 10px;
            border-radius: 12px;
          }
          .ad-kpi-card {
            flex: 1 1 100%;
            padding: 14px 12px;
          }
          .ad-kpi-val {
            font-size: 24px;
          }
          .ad-card-header {
            margin-bottom: 14px;
          }
        }
      `}</style>

      {/* ══════════════════════════════════════════════
          MAIN CONTENT
      ══════════════════════════════════════════════ */}
      <div className="ad-scroll-container">

        {/* Minimal top bar: title + refresh/error, since this page had no header before */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: isMobile ? '16px 16px 0' : '20px 28px 0',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: t.text }}>Admin Dashboard</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {error && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: t.red }}>
                <Inbox size={13} /> {error}
              </span>
            )}
            <button
              onClick={fetchDashboard}
              disabled={loading}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'rgba(255,255,255,0.05)',
                border: `1px solid ${t.cardBorder}`,
                borderRadius: '8px', padding: '7px 14px',
                color: t.textMuted, fontSize: '12px', fontWeight: 700,
                cursor: loading ? 'default' : 'pointer',
              }}
            >
              <RefreshCw size={13} className={loading ? 'admin-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {/* Page Content */}
        <main className="ad-main-content">

          {/* ① KPI Row */}
          <div className="ad-grid-row">
            <KpiCard
              title="MRR"
              value={`$${Math.round(mrr).toLocaleString()}`}
              change={`${mrrGrowth >= 0 ? '+' : ''}${mrrGrowth}%`}
              changeLabel="Driven by new signups"
              positive={mrrGrowth >= 0}
              icon={CreditCard}
              iconColor={t.accent}
              loading={loading}
            />
            <KpiCard
              title="Total Customers"
              value={totalCustomers.toLocaleString()}
              change={`${customersGrowth >= 0 ? '+' : ''}${customersGrowth}%`}
              changeLabel="Active subscriptions"
              positive={customersGrowth >= 0}
              icon={Users}
              iconColor={t.purple}
              loading={loading}
            />
            <KpiCard
              title="New This Month"
              value={newThisMonth.toLocaleString()}
              change={`${newGrowth >= 0 ? '+' : ''}${newGrowth}%`}
              changeLabel="Vs. last month"
              positive={newGrowth >= 0}
              icon={UserPlus}
              iconColor={t.blue}
              loading={loading}
            />
            <KpiCard
              title="Churned This Month"
              value={churnedThisMonth.toLocaleString()}
              change={`${churnedGrowth >= 0 ? '+' : ''}${churnedGrowth}%`}
              changeLabel="Churn stabilizing"
              positive={false}
              icon={UserMinus}
              iconColor={t.amber}
              loading={loading}
            />
          </div>

          {/* ② Revenue Trend + MRR Movement */}
          <div className="ad-grid-row">
            <Card className="ad-card-chart">
              <CardHeader title="Revenue Trend" subtitle="Monthly recurring revenue over time" />
              <RevenueChart activeTab={activeTab} setActiveTab={setActiveTab} trend={revenueTrend} loading={loading} />
            </Card>
            <Card className="ad-card-mrr">
              <CardHeader title="MRR Movement" subtitle="Current month breakdown" />
              <MrrMovement movement={mrrMovement} loading={loading} />
            </Card>
          </div>

          {/* ③ Onboarding Funnel + At-Risk Customers */}
          <div className="ad-grid-row">
            <Card className="ad-card-funnel">
              <CardHeader title="Onboarding Funnel" subtitle="Conversion rates across stages" />
              <OnboardingFunnel funnel={onboardingFunnel} loading={loading} />
            </Card>
            <Card className="ad-card-at-risk">
              <CardHeader
                title="At-Risk Customers"
                subtitle="High churn probability"
                action={
                  <button style={{ background: 'none', border: 'none', fontSize: '12px', color: t.accent, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                    View All
                  </button>
                }
              />
              <AtRiskCustomers rows={atRiskCustomers} loading={loading} />
            </Card>
          </div>

          {/* ④ Recent Signups */}
          <Card className="ad-card-signups">
            <CardHeader
              title="Recent Signups"
              subtitle="Latest user registrations"
              action={
                <button style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: 'rgba(255,255,255,0.05)',
                  border: `1px solid ${t.cardBorder}`,
                  borderRadius: '8px', padding: '7px 14px',
                  color: t.textMuted, fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                }}>
                  <Download size={13} /> Export CSV
                </button>
              }
            />
            <RecentSignups signups={dashboardData?.recent_signups || dashboardData?.signups || []} loading={loading} />
          </Card>

        </main>
      </div>
    </div>
  );
};


export default AdminDashboard;